import { Injectable, inject } from '@angular/core';
import { HttpContext } from '@angular/common/http';
import { explainedInPlace, rethrowAsRefusal } from '@core/helpers/installation-refusal.helper';
import { TelemetryPeriod } from '@core/constants/telemetry-route.constant';
import { SKIP_GLOBAL_ERROR_SNACKBAR_FOR_STATUS } from '@core/interceptors/http-error.interceptor';
import {
  ChannelUsage,
  Installation,
  InstallationFilter,
  InstallationsResponse,
  InstallationTelemetry,
} from '@core/models/installation.model';
import { NewInstallation } from '@core/models/installation-write.model';
import { ApiService } from '@core/services/api.service';
import { Observable, catchError } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class InstallationService {
  private readonly apiService = inject(ApiService);

  list(filter: InstallationFilter): Observable<InstallationsResponse> {
    return this.apiService.get<InstallationsResponse>('/installations/', { ...filter });
  }

  get(installationOid: string): Observable<Installation> {
    return this.apiService.get<Installation>(`/installations/${installationOid}`);
  }

  // Fails with an InstallationRefusal, which the dialog explains in place.
  create(body: NewInstallation): Observable<Installation> {
    return this.apiService
      .postRaw<Installation>('/installations/', body, { context: explainedInPlace() })
      .pipe(catchError(rethrowAsRefusal));
  }

  // Links a registered device through the installation, which leaves the device's own location and
  // status alone. Fails with an InstallationRefusal like the writes above.
  linkDevice(installationOid: string, deviceId: string | number): Observable<unknown> {
    return this.apiService
      .postRaw<unknown>(
        `/installations/${installationOid}/devices`,
        { device_id: Number(deviceId) },
        { context: explainedInPlace() },
      )
      .pipe(catchError(rethrowAsRefusal));
  }

  channelUsage(deviceId: string | number): Observable<ChannelUsage> {
    return this.apiService.get<ChannelUsage>('/installations/channel-usage', { deviceId });
  }

  // Hidden points always come along so the data explorer can offer them without a second
  // request; the insights page filters them out itself. A 404 means the installation's telemetry
  // is not shared with the user, which the page explains itself.
  telemetry(installationOid: string, period: TelemetryPeriod): Observable<InstallationTelemetry> {
    return this.apiService.getRaw<InstallationTelemetry>(
      `/telemetry/installations/${installationOid}/telemetry/${period.granularity}/${period.period}`,
      { includeHidden: true },
      new HttpContext().set(SKIP_GLOBAL_ERROR_SNACKBAR_FOR_STATUS, [404]),
    );
  }
}
